import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  // Get session ID from URL parameters
  const getSessionId = () => {
    const params = new URLSearchParams(location.search);
    return params.get('session_id');
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      toast({
        title: "Payment status check timed out",
        description: "Please check your email for confirmation or contact support at Corporate.dccorp@gmail.com.",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast({
          title: "Payment successful!",
          description: "Thank you for your purchase. Your order has been confirmed.",
        });
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast({
          title: "Payment session expired",
          description: "Your payment session has expired. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('processing');
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);

    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      toast({
        title: "Error checking payment status",
        description: "Please try refreshing the page or contact support at Corporate.dccorp@gmail.com.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const sessionId = getSessionId();
    if (sessionId && user) {
      pollPaymentStatus(sessionId);
    } else if (!sessionId) {
      setPaymentStatus('invalid');
    }
  }, [user]);

  const renderStatusContent = () => {
    switch (paymentStatus) {
      case 'checking':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-400 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-4">Checking payment status...</h2>
              <p className="text-white/70">Please wait while we verify your payment.</p>
            </CardContent>
          </Card>
        );

      case 'processing':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="animate-pulse rounded-full h-16 w-16 bg-yellow-400 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">⏳</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Payment processing...</h2>
              <p className="text-white/70 mb-4">
                Your payment is being processed. This may take a few moments.
              </p>
              <p className="text-white/60 text-sm">
                Attempt {pollAttempts} of 10
              </p>
            </CardContent>
          </Card>
        );

      case 'success':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="rounded-full h-16 w-16 bg-green-500 mx-auto mb-6 flex items-center justify-center">
                <span className="text-3xl text-white">✓</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Payment Successful!</h2>
              <p className="text-white/70 mb-6">
                Thank you for your purchase. Your order has been confirmed and you will receive a confirmation email shortly.
              </p>
              
              {paymentDetails && (
                <div className="bg-white/5 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-white/70">Amount:</div>
                    <div className="text-white font-semibold">
                      ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
                    </div>
                    <div className="text-white/70">Status:</div>
                    <div className="text-green-400 font-semibold">Paid</div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 justify-center">
                <Link to="/profile">
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                    View Orders
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="border-white/50 text-white bg-white/10">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        );

      case 'expired':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="rounded-full h-16 w-16 bg-orange-500 mx-auto mb-6 flex items-center justify-center">
                <span className="text-3xl text-white">⏱</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Payment Session Expired</h2>
              <p className="text-white/70 mb-6">
                Your payment session has expired. Please try placing your order again.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/dashboard">
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                    Try Again
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        );

      case 'error':
      case 'timeout':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="rounded-full h-16 w-16 bg-red-500 mx-auto mb-6 flex items-center justify-center">
                <span className="text-3xl text-white">❌</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">
                {paymentStatus === 'timeout' ? 'Payment Check Timeout' : 'Error'}
              </h2>
              <p className="text-white/70 mb-6">
                {paymentStatus === 'timeout' 
                  ? 'We could not verify your payment status. Please check your email for confirmation.'
                  : 'There was an error checking your payment status. Please try again or contact support at Corporate.dccorp@gmail.com.'
                }
              </p>
              <div className="flex gap-4 justify-center">
                <Button 
                  onClick={() => window.location.reload()}
                  className="bg-gradient-to-r from-blue-500 to-purple-500   text-white"
                >
                  Retry
                </Button>
                <Link to="/profile">
                  <Button variant="outline" className="border-white/50 text-white bg-white/10">
                    Check Orders
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        );

      case 'invalid':
        return (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8 text-center">
              <div className="rounded-full h-16 w-16 bg-red-500 mx-auto mb-6 flex items-center justify-center">
                <span className="text-3xl text-white">⚠</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Invalid Payment Link</h2>
              <p className="text-white/70 mb-6">
                This payment link is invalid or has expired. Please try placing your order again.
              </p>
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-blue-500 to-purple-500   text-white">
                  Return to Store
                </Button>
              </Link>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Please log in to continue</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/profile">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Profile
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {renderStatusContent()}
      </div>
    </div>
  );
};

export default PaymentSuccess;